"use client";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { PageSectionHeader } from '../structure/page-blocks';
import { FormEngine } from '../sitebuilder/form/formengine';
import { FormButton } from '../sitebuilder/form/formcomponents';
import { processJSON } from "../sitebuilder/form/formsubmit";
import '../sitebuilder/form/form.css';
import { MicroInteractions } from '../foundation/microinteractions';
import { Modal, handleModalOpen } from '../elements/modal';
import { Table } from "../elements/table";
import { getCart, getShippingInfo, setShippingInfo, setDiscountCodes, getRemoteDiscountCodes, getCheckoutData, removeFromShoppingCart, clearShoppingCart, getCartItemCount, increaseQuantityInCart, decreaseQuantityInCart } from "./shoppingcart.functions";
import { formatAsUSD, formatAsHundredths } from "../foundation/utilities";
import { usePixelatedConfig } from '../config/config.client';
import './usps.components';
import { SmartImage } from '../elements/smartimage';
import { getActivePaymentProvider } from './shoppingcart.providers';
import personalInfoData from "./checkout.personal.info.json";
import discountInfoData from "./checkout.discount.info.json";
import "./shoppingcart.css";
const debug = false;
/* ================================================ */
/* ========== SHOPPING CART UI COMPONENT ========== */
/* ================================================ */
/**
 * ShoppingCart — page-level shopping cart UI that handles items, shipping, and checkout flows.
 *
 * Props:
 * @param {function} [props.onPaymentCapture] - Optional callback invoked to capture payment on the server.
 */
ShoppingCart.propTypes = {
    /** Optional callback invoked to capture payment on the server */
    onPaymentCapture: PropTypes.func,
    /** Optional app-specific custom subtotal discount */
    subtotalDiscountCustom: PropTypes.number,
    /** Optional override for the checkout personal info form */
    personalInfoForm: PropTypes.object,
    /** Optional override for the checkout discount form */
    discountInfoForm: PropTypes.object,
    /** Optional site metadata used for shipping origin defaults */
    siteInfo: PropTypes.object,
    /** Optional override for the checkout shipping form */
    shippingInfoForm: PropTypes.object,
    /** Optional override for additional checkout info fields */
    additionalInfoForm: PropTypes.object,
    /** Whether to show the discount form fields (default: true) */
    showDiscountForm: PropTypes.bool,
};
export function ShoppingCart(props) {
    const config = usePixelatedConfig();
    const shippingFormRef = useRef(null);
    const [shippingFormRevision, setShippingFormRevision] = useState(0);
    const effectiveConfig = config || {};
    const siteInfo = props.siteInfo;
    const activeProvider = getActivePaymentProvider(effectiveConfig);
    const PaymentProviderComponent = activeProvider ? activeProvider.component : null;
    const shippingDefaults = {
        originPostalCode: siteInfo?.address?.postalCode ?? undefined,
        originCountry: siteInfo?.address?.addressCountry ?? undefined,
    };
    const checkoutDiscountCustom = props.subtotalDiscountCustom ?? 0;
    const checkoutDataForProvider = getCheckoutData(shippingDefaults, effectiveConfig?.integrations?.shoppingcart, checkoutDiscountCustom);
    const personalInfoFormData = props.personalInfoForm ?? personalInfoData;
    const discountInfoFormData = props.discountInfoForm ?? discountInfoData;
    const additionalInfoFormData = props.additionalInfoForm;
    const showDiscount = props.showDiscountForm !== false;
    const hasUSPSShipping = Boolean(effectiveConfig?.integrations?.usps);
    const [shoppingCart, setShoppingCart] = useState([]);
    const [shippingData, setShippingData] = useState();
    const [orderData, setOrderData] = useState();
    const [progressStep, setProgressStep] = useState("EmptyCart");
    const [uspsRates, setUspsRates] = useState([]);
    const [selectedUspsRate, setSelectedUspsRate] = useState(null);
    const lastEmailSentForOrderRef = useRef(null);
    const showShippingSubForm = shoppingCart.some((item) => item?.itemIsShippable !== false);
    const discountFormData = checkoutDiscountCustom > 0 ? {
        ...discountInfoFormData,
        fields: [
            ...(discountInfoFormData?.fields ?? []),
            {
                component: "FormLabel",
                props: {
                    id: "checkout_discount_custom_label",
                    label: `Discount applied: ${formatAsUSD(checkoutDiscountCustom)}`,
                    className: "pix-cart-discount-applied"
                }
            }
        ]
    } : discountInfoFormData;
    const checkoutContinueButtonField = (!showShippingSubForm || selectedUspsRate) ? [{
            component: 'FormButton',
            props: {
                id: 'saveShippingInfo',
                type: 'submit',
                text: 'Continue to Checkout',
                className: 'pix-cart-button',
            },
        }] : [];
    const shippingFormFields = [
        ...(personalInfoFormData?.fields ?? []),
        ...(showDiscount ? (discountFormData?.fields ?? []) : []),
        ...(additionalInfoFormData ? [{
                component: "FormSectionHeader",
                props: {
                    "title": "Additional Checkout Info"
                }
            }] : []),
        ...(additionalInfoFormData?.fields ?? [])
    ];
    const checkoutFormData = {
        properties: personalInfoFormData?.properties ?? {},
        fields: [
            ...shippingFormFields,
            ...(showShippingSubForm && hasUSPSShipping ? [{
                    component: 'USPSShippingForm',
                    props: {
                        shippingDefaults,
                        formRef: shippingFormRef,
                        formRevision: shippingFormRevision,
                        shoppingCart,
                        rates: uspsRates,
                        selectedRate: selectedUspsRate,
                        onRatesChange: setUspsRates,
                        onSelectedRateChange: setSelectedUspsRate,
                    },
                }] : []),
            ...checkoutContinueButtonField,
        ]
    };
    const checkoutShippingWeight = checkoutDataForProvider.shippingWeight ?? 0;
    // Optional PayPal client ID to enable the PayPal checkout button.
    // This value is pulled from config.integrations.paypal.payPalApiKey or sandboxPayPalApiKey.
    const paymentProviderProps = activeProvider ? {
        ...activeProvider.getProps(effectiveConfig, checkoutDataForProvider, {
            onPaymentCapture: props.onPaymentCapture ?? undefined,
        }),
    } : {};
    function SetProgressStep(step) {
        if (step) {
            setProgressStep(step);
        }
        else {
            const currentCart = getCart();
            const hasShoppingCart = currentCart.length > 0;
            if (debug)
                console.log("hasShoppingCart", hasShoppingCart);
            const hasShippingInfo = Object.keys(getShippingInfo()).length > 0;
            if (debug)
                console.log("hasShippingInfo", hasShippingInfo);
            const hasOrderData = orderData && ((Array.isArray(orderData) && orderData.length > 0) || (!Array.isArray(orderData) && Object.keys(orderData).length > 0));
            if (debug)
                console.log("hasOrderData", hasOrderData);
            if (debug && orderData && !Array.isArray(orderData))
                console.log(Object.keys(orderData).length);
            if (hasOrderData) {
                setProgressStep("ThankYou");
            }
            else if (hasShippingInfo && hasShoppingCart) {
                setProgressStep("Checkout");
            }
            else if (hasShoppingCart) {
                setProgressStep("CartItems");
            }
            else {
                setProgressStep("EmptyCart");
            }
        }
    }
    /* useEffect(() => {
        // UPDATE LOCALSTORAGE IF SHOPPINGCART STATE CHANGES
        setCart(shoppingCart ?? []);
    }, [shoppingCart]); */
    /* useEffect(() => {
        // UPDATE LOCALSTORAGE IF SHIPPINGDATA STATE CHANGES
        setShippingInfo(shippingInfo);
    }, [shippingInfo]); */
    useEffect(() => {
        // UPDATE DISCOUNT CODES ON EACH PAGE LOAD
        (async () => {
            const contentfulConfig = config?.integrations?.contentful;
            setDiscountCodes(await getRemoteDiscountCodes(contentfulConfig));
        })();
        // UPDATE SHOPPINGCART AND SHIPPINGDATA STATES IF LOCALSTORAGE CHANGES
        function handleStorageChange() {
            setShoppingCart(getCart());
            setShippingData(getShippingInfo());
            SetProgressStep();
        }
        window.addEventListener('storage', handleStorageChange);
        window.dispatchEvent(new Event('storage'));
        return () => {
            window.removeEventListener('storage', handleStorageChange);
        };
    }, []);
    useEffect(() => {
        // LOAD THE SHIPPING INFO FORM WITH VALUES IF SHIPPING INFO HAS ALREADY BEEN SAVED
        if (shippingData && progressStep === "CartItems") {
            Object.entries(shippingData).forEach(([key, value]) => {
                const elements = Array.from(document.getElementsByName(key));
                if (!elements.length)
                    return;
                if (elements[0].type === 'radio' || elements[0].type === 'checkbox') {
                    const selectedValues = Array.isArray(value)
                        ? value.map((item) => String(item ?? '').trim())
                        : [String((value ?? '')).trim()];
                    elements.forEach(el => {
                        const elementValue = String(el.value ?? '').trim();
                        if (el.type === 'radio') {
                            el.checked = selectedValues.includes(elementValue);
                        }
                        else {
                            el.checked = selectedValues.includes(elementValue) || selectedValues.includes('true') || selectedValues.includes('on');
                        }
                    });
                }
                else {
                    elements.forEach(el => {
                        el.value = String(value ?? '');
                    });
                }
            });
        }
    }, [progressStep, shippingData]);
    useEffect(() => {
        if (progressStep !== 'ThankYou' || !orderData) {
            return;
        }
        const emailMarker = orderData?.id || orderData?.orderID || JSON.stringify(orderData);
        if (lastEmailSentForOrderRef.current === emailMarker) {
            return;
        }
        const cartConfig = config?.integrations?.shoppingcart;
        const json = {
            'to': cartConfig?.orderTo,
            'from': cartConfig?.orderFrom,
            'subject': cartConfig?.orderSubject,
            'formName': cartConfig?.orderFormName,
            'domain': cartConfig?.orderDomain,
            'orderData': JSON.stringify(orderData, null, 2),
        };
        processJSON(json);
        setShippingFormRevision((revision) => revision + 1);
        lastEmailSentForOrderRef.current = emailMarker;
    }, [progressStep, orderData, config]);
    useEffect(() => {
        if (progressStep !== 'Checkout') {
            return;
        }
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [progressStep]);
    useEffect(() => {
        if (progressStep !== 'CartItems' || !shippingData) {
            return;
        }
        const savedRates = shippingData.uspsRates;
        if (Array.isArray(savedRates) && savedRates.length > 0 && uspsRates.length === 0) {
            setUspsRates(savedRates);
        }
        const savedSelectedRate = shippingData.selectedUspsRate;
        if (savedSelectedRate && !selectedUspsRate) {
            setSelectedUspsRate(savedSelectedRate);
        }
    }, [progressStep, shippingData, selectedUspsRate, uspsRates.length]);
    function onShippingSubmit(event) {
        event.preventDefault();
        const form = event.target;
        const formObject = Object.fromEntries(new FormData(form));
        setShippingInfo({
            ...formObject,
            uspsRates,
            selectedUspsRate,
        });
    }
    /**
     * handlePaymentSuccess — invoked after a payment provider reports success.
     *
     * @param {object} [props.data] - Payment approval payload returned by the provider.
     */
    handlePaymentSuccess.propTypes = {
        data: PropTypes.any.isRequired
    };
    function handlePaymentSuccess(props) {
        if (debug)
            console.log("Handling payment success");
        setOrderData(props.data);
        clearShoppingCart();
        SetProgressStep("ThankYou");
    }
    if (progressStep === "ThankYou") {
        // ========== SENDMAIL ==========
        // ========== THANK YOU ==========
        return (_jsxs("div", { className: "pix-cart", children: [_jsx(PageSectionHeader, { title: "Shopping Cart : " }), _jsx("br", {}), _jsx(ShoppingCartReceipt, { orderData: orderData, config: config })] }));
    }
    else if (progressStep === "Checkout") {
        // ========== CHECKOUT ==========
        return (_jsxs("div", { className: "pix-cart", children: [_jsx(PageSectionHeader, { title: "Checkout Summary : " }), PaymentProviderComponent ? (_jsx(CheckoutSummary, { effectiveCheckoutData: checkoutDataForProvider, checkoutShippingWeight: checkoutShippingWeight, PaymentProviderComponent: PaymentProviderComponent, paymentProviderProps: paymentProviderProps, onBackToCart: () => SetProgressStep("CartItems"), handlePaymentSuccess: handlePaymentSuccess })) : (_jsx("div", { children: "No payment provider is configured. Add PayPal or Square configuration to pixelated.config.json." }))] }));
    }
    else if (progressStep === "CartItems") {
        return (_jsxs("div", { className: "pix-cart", children: [_jsx(PageSectionHeader, { title: "Shopping Cart : " }), _jsx(ShoppingCartItems, { items: shoppingCart ?? [] }), _jsx(ShoppingCartTotals, { checkoutData: checkoutDataForProvider }), _jsx("br", {}), _jsx("div", { className: "pix-cart-actions", children: _jsx(FormButton, { className: "pix-cart-button", type: "button", id: "clearCart", text: "Clear Cart", onClick: () => clearShoppingCart() }) }), _jsx("br", {}), _jsx("hr", {}), _jsx("br", {}), _jsx(PageSectionHeader, { title: "Checkout Info" }), _jsx(CheckoutInfoForm, { checkoutFormData: checkoutFormData, formRef: shippingFormRef, onFieldChange: () => setShippingFormRevision((revision) => revision + 1), onShippingSubmit: onShippingSubmit })] }));
    }
    else {
        // ========== EMPTY SHOPPING CART ==========
        return (_jsxs("div", { className: "pix-cart", children: [_jsx(PageSectionHeader, { title: "Shopping Cart : " }), _jsx("br", {}), _jsx("div", { className: "centered", children: "No items in your shopping cart" })] }));
    }
}
/* ========================================
====== SHOPPING CART ITEMS COMPONENT ======
======================================== */
/**
 * ShoppingCartItems — renders the list of items in the shopping cart.
 *
 * @param {array} [props.items] - Array of shopping cart line items to render.
 * Each item should have the shape: { itemID, itemURL, itemTitle, itemImageURL, itemQuantity, itemCost, itemIsShippable, itemWeight, itemWeightUnit }
 */
ShoppingCartItems.propTypes = {
    items: PropTypes.arrayOf(PropTypes.shape({
        itemID: PropTypes.string.isRequired,
        itemURL: PropTypes.string,
        itemTitle: PropTypes.string.isRequired,
        itemImageURL: PropTypes.string,
        itemQuantity: PropTypes.number.isRequired,
        itemCost: PropTypes.number.isRequired,
        itemIsShippable: PropTypes.bool,
        itemWeight: PropTypes.number,
        itemWeightUnit: PropTypes.string,
    })).isRequired,
};
export function ShoppingCartItems({ items }) {
    if (debug)
        console.log('Painting Shopping Cart Items');
    return (_jsx(_Fragment, { children: items.map((item) => item ? (_jsx(ShoppingCartItem, { item: item }, item.itemID)) : null) }));
}
/* ================================================ */
/* =========== SHOPPING CART TOTALS =============== */
/* ================================================ */
/**
 * ShoppingCartTotals — Render a summary section showing item count, subtotal, discounts, and estimated total.
 *
 * @param {object} [props.checkoutData] - The calculated checkout data including totals and items.
 */
ShoppingCartTotals.propTypes = {
    checkoutData: PropTypes.object.isRequired,
};
export function ShoppingCartTotals({ checkoutData }) {
    if (!checkoutData)
        return null;
    const itemCount = checkoutData.items?.reduce((sum, item) => sum + (item.itemQuantity || 0), 0) || 0;
    const currency = checkoutData.currency || 'USD';
    return (_jsxs("div", { className: "pix-cart-totals", children: [_jsx(PageSectionHeader, { title: "Subtotal : " }), _jsxs("div", { className: "pix-cart-totals-content", children: [_jsxs("div", { className: "pix-cart-total-row", children: [_jsx("b", { children: "Total Items: " }), _jsx("span", { children: itemCount })] }), _jsxs("div", { className: "pix-cart-total-row", children: [_jsx("b", { children: "Subtotal: " }), _jsxs("span", { children: [formatAsUSD(checkoutData.subtotal), " ", currency] })] }), checkoutData.subtotal_discount > 0 && (_jsxs("div", { className: "pix-cart-total-row pix-cart-discount", children: [_jsx("b", { children: "Discounts: " }), _jsxs("span", { children: ["-", formatAsUSD(checkoutData.subtotal_discount), " ", currency] })] })), _jsxs("div", { className: "pix-cart-total-row pix-cart-grand-total", children: [_jsx("b", { children: "Estimated Total: " }), _jsxs("span", { children: [formatAsUSD(checkoutData.total), " ", currency] })] })] })] }));
}
/* ================================================ */
/* ========= CHECKOUT INFO FORM COMPONENT ========= */
/* ================================================ */
/**
 * CheckoutInfoForm — renders the form for collecting checkout information, including personal info, discounts, and additional fields.
 *
 * @param {object} [props.checkoutFormData] - Form configuration object containing fields and properties for the checkout form.
 * @param {function} [props.onShippingSubmit] - Callback function invoked when the checkout form is submitted.
 */
CheckoutInfoForm.propTypes = {
    checkoutFormData: PropTypes.object.isRequired,
    formRef: PropTypes.any,
    onFieldChange: PropTypes.func,
    onShippingSubmit: PropTypes.func.isRequired,
};
export function CheckoutInfoForm({ checkoutFormData, formRef, onFieldChange, onShippingSubmit }) {
    return (_jsx(FormEngine, { name: "checkout_shipping", id: "checkout_shipping", formRef: formRef, onFieldChange: onFieldChange, formData: checkoutFormData, onSubmitHandler: onShippingSubmit }));
}
/* ================================================ */
/* ========== CHECKOUT SUMMARY COMPONENT ========== */
/* ================================================ */
/**
 * CheckoutSummary — renders the checkout summary including order details and payment options.
 *
 * @param {object} [props.effectiveCheckoutData] - The computed checkout data including totals and discounts.
 * @param {number} [props.checkoutShippingWeight] - The total shipping weight of the order.
 * @param {React.ComponentType} [props.PaymentProviderComponent] - The payment provider component to render for processing payments.
 * @param {object} [props.paymentProviderProps] - The props to pass to the payment provider component.
 * @param {function} [props.onBackToCart] - Callback function invoked when the user wants to go back to the cart from the checkout summary.
 * @param {function} [props.handlePaymentSuccess] - Callback function invoked when a payment is successfully processed.
 */
CheckoutSummary.propTypes = {
    effectiveCheckoutData: PropTypes.object.isRequired,
    checkoutShippingWeight: PropTypes.number.isRequired,
    PaymentProviderComponent: PropTypes.elementType.isRequired,
    paymentProviderProps: PropTypes.object.isRequired,
    onBackToCart: PropTypes.func.isRequired,
    handlePaymentSuccess: PropTypes.func.isRequired,
};
export function CheckoutSummary({ effectiveCheckoutData, checkoutShippingWeight, PaymentProviderComponent, paymentProviderProps, onBackToCart, handlePaymentSuccess, }) {
    return (_jsxs(_Fragment, { children: [effectiveCheckoutData && _jsx(CheckoutItems, { ...effectiveCheckoutData, shippingWeight: checkoutShippingWeight }), _jsx("br", {}), _jsxs("div", { className: "pix-cart-actions", children: [_jsx(FormButton, { className: "pix-cart-button", type: "button", id: "backToCart", text: "<= Back To Cart", onClick: onBackToCart }), _jsx(FormButton, { className: "pix-cart-button", type: "button", id: "clearCart", text: "Clear Cart", onClick: () => clearShoppingCart() })] }), _jsx("br", {}), _jsx(PageSectionHeader, { title: "Payment Options : " }), _jsx(PaymentProviderComponent, { ...paymentProviderProps, checkoutData: effectiveCheckoutData, onApprove: handlePaymentSuccess })] }));
}
/* ================================================ */
/* ======== SHOPPING CART RECEIPT COMPONENT ======= */
/* ================================================ */
/**
 * ShoppingCartReceipt — renders the receipt details after a successful order, or a thank you message if no order data is available.
 *
 * @param {object} [props.orderData] - The order data returned after a successful payment, used to build the receipt.
 * @param {object} [props.config] - Optional configuration object that may be used to customize receipt rendering.
 */
ShoppingCartReceipt.propTypes = {
    orderData: PropTypes.any.isRequired,
    config: PropTypes.any,
};
export function ShoppingCartReceipt({ orderData, config }) {
    if (!orderData) {
        return (_jsx("div", { children: _jsx("h3", { children: "Thank you for your payment!" }) }));
    }
    const receipt = buildReceiptData(orderData, config);
    if (receipt) {
        return (_jsxs("div", { children: [_jsx("h3", { children: "Thank you for your payment!" }), _jsx("br", {}), _jsx("h3", { style: { textAlign: 'center' }, children: "Your Receipt:" }), renderReceiptTable(receipt)] }));
    }
    return (_jsxs("div", { children: [_jsx("h3", { children: "Thank you for your payment!" }), _jsx("pre", { style: { whiteSpace: 'pre-wrap', wordBreak: 'break-word' }, children: JSON.stringify(orderData, null, 2) })] }));
}
/* ================================================ */
/* ========= SHOPPING CART ITEM COMPONENT ========= */
/* ================================================ */
/**
 * ShoppingCartItem — Render a single cart line item showing thumbnail, title, quantity and price.
 *
 * @param {shape} [props.item] - Shopping cart line item with id, title, image, quantity and cost.
 * @param {string} [props.itemID] - Unique identifier for the cart item.
 * @param {string} [props.itemURL] - Optional item detail URL to link the title and image.
 * @param {string} [props.itemTitle] - Display title for the item.
 * @param {string} [props.itemImageURL] - Thumbnail image URL for the item.
 * @param {number} [props.itemQuantity] - Quantity of this line item in the cart.
 * @param {number} [props.itemCost] - Per-item cost (numeric) used to compute totals.
 */
ShoppingCartItem.propTypes = {
    /** Shopping cart line item object */
    item: PropTypes.shape({
        /** Unique item id */
        itemID: PropTypes.string.isRequired,
        /** Optional URL for the item details */
        itemURL: PropTypes.string,
        /** Item display title */
        itemTitle: PropTypes.string.isRequired,
        /** Thumbnail image URL */
        itemImageURL: PropTypes.string,
        /** Line item quantity */
        itemQuantity: PropTypes.number.isRequired,
        /** Stock limit for the item */
        itemInventory: PropTypes.number,
        /** Per-item price */
        itemCost: PropTypes.number.isRequired,
        /** Whether the item can be shipped */
        itemIsShippable: PropTypes.bool,
        /** Item weight used for shipping calculations */
        itemWeight: PropTypes.number,
        /** Item weight unit (lb, kg, oz) */
        itemWeightUnit: PropTypes.string,
    }).isRequired
};
export function ShoppingCartItem(props) {
    const thisItem = props.item;
    const thisItemTarget = "_self"; // "_blank"
    const config = usePixelatedConfig();
    return (_jsxs("div", { className: "pix-cart-item row-12col", children: [_jsx("div", { className: "pix-cart-item-photo grid-s1-e4", children: thisItem.itemURL && thisItem.itemImageURL
                    ? _jsx("a", { href: thisItem.itemURL, target: thisItemTarget, rel: "noopener noreferrer", children: _jsx(SmartImage, { src: thisItem.itemImageURL, alt: thisItem.itemTitle, cloudinaryEnv: config?.integrations?.cloudinary?.product_env, cloudinaryDomain: config?.integrations?.cloudinary?.baseUrl, cloudinaryTransforms: config?.integrations?.cloudinary?.transforms }) })
                    : thisItem.itemImageURL
                        ? (_jsx(SmartImage, { src: thisItem.itemImageURL, title: thisItem.itemTitle, alt: thisItem.itemTitle, cloudinaryEnv: config?.integrations?.cloudinary?.product_env, cloudinaryDomain: config?.integrations?.cloudinary?.baseUrl, cloudinaryTransforms: config?.integrations?.cloudinary?.transforms }))
                        : _jsx(_Fragment, {}) }), _jsxs("div", { className: "grid-s4-e11", children: [_jsx("div", { className: "pix-cart-item-header", children: _jsx("span", { children: thisItem.itemURL
                                ? _jsx("a", { href: thisItem.itemURL, target: thisItemTarget, rel: "noopener noreferrer", children: _jsx("h2", { className: "", children: thisItem.itemTitle }) })
                                : _jsx("h2", { className: "", children: thisItem.itemTitle }) }) }), _jsxs("div", { className: "pix-cart-item-details grid12", children: [_jsx("br", {}), _jsxs("div", { children: [_jsx("b", { children: "Item ID: " }), thisItem.itemID] }), _jsxs("div", { className: "pix-cart-item-quantity", children: [_jsx("b", { children: "Quantity: " }), _jsxs("div", { className: "pix-cart-quantity-controls", children: [_jsx(FormButton, { className: "pix-cart-quantity-button", type: "button", id: `btn-dec-${thisItem.itemID}`, text: "-", onClick: () => {
                                                    if (thisItem.itemQuantity > 1) {
                                                        decreaseQuantityInCart(thisItem);
                                                    }
                                                    else {
                                                        removeFromShoppingCart(thisItem);
                                                    }
                                                } }), _jsx("span", { className: "pix-cart-quantity-value", children: thisItem.itemQuantity }), _jsx(FormButton, { className: "pix-cart-quantity-button", type: "button", id: `btn-inc-${thisItem.itemID}`, text: "+", disabled: thisItem.itemQuantity >= (thisItem.itemInventory || 1), onClick: () => increaseQuantityInCart(thisItem) })] })] }), thisItem.itemIsShippable ? (_jsxs("div", { children: [_jsx("b", { children: "Weight: " }), thisItem.itemWeight ?? 'N/A', " ", thisItem.itemWeightUnit || 'lb'] })) : (_jsxs("div", { children: [_jsx("b", { children: "Shipping:" }), " Non-shippable item"] }))] })] }), _jsxs("div", { className: "grid-s11-e13", children: [_jsx("div", { className: "pix-cart-item-price", children: formatAsUSD((thisItem.itemCost ?? 0) * thisItem.itemQuantity) }), thisItem.itemQuantity > 1 && (_jsxs("div", { className: "pix-cart-item-price-each", children: [formatAsUSD(thisItem.itemCost ?? 0), " ea."] })), _jsx("br", {}), _jsx("div", { children: _jsx(FormButton, { className: "pix-cart-button", type: "button", id: `btn-rm-${thisItem.itemID}`, text: "Remove", onClick: () => removeFromShoppingCart(thisItem) }) })] })] }));
}
/* ================================================ */
/* =========== CHECKOUT ITEMS COMPONENT =========== */
/* ================================================ */
/**
 * CheckoutItems — Display a checkout summary with itemized lines and shipping information.
 *
 * @param {arrayOf} [props.items] - Cart items included in the checkout summary.
 * @param {shape} [props.shippingTo] - Shipping address object with name, street1, city, state and zip.
 * @param {number} [props.subtotal_discount] - Discount amount applied to subtotal.
 * @param {number} [props.subtotal] - Subtotal amount before shipping and taxes.
 * @param {number} [props.shippingCost] - Shipping cost for the order.
 * @param {number} [props.handlingFee] - Optional handling fees.
 * @param {number} [props.salesTax] - Sales tax amount.
 * @param {number} [props.total] - Final total amount charged.
 */
CheckoutItems.propTypes = {
    /** Array of cart items to summarize */
    items: PropTypes.arrayOf(PropTypes.shape({
        /** Item identifier */
        itemID: PropTypes.string.isRequired,
        /** Item detail URL (optional) */
        itemURL: PropTypes.string,
        /** Item title */
        itemTitle: PropTypes.string.isRequired,
        /** Item image URL */
        itemImageURL: PropTypes.string,
        /** Quantity for this item */
        itemQuantity: PropTypes.number.isRequired,
        /** Stock limit for the item */
        itemInventory: PropTypes.number,
        /** Per-item price */
        itemCost: PropTypes.number.isRequired,
    })).isRequired,
    /** Shipping address object */
    shippingTo: PropTypes.shape({
        name: PropTypes.string.isRequired,
        street1: PropTypes.string.isRequired,
        city: PropTypes.string.isRequired,
        state: PropTypes.string.isRequired,
        zip: PropTypes.string.isRequired,
        country: PropTypes.string,
        email: PropTypes.string,
        phone: PropTypes.string,
        shippingMethod: PropTypes.string,
    }).isRequired,
    /** Optional shipping weight for the order */
    shippingWeight: PropTypes.number,
    /** Discount amount applied to subtotal */
    subtotal_discount: PropTypes.number.isRequired,
    /** Subtotal amount before shipping and taxes */
    subtotal: PropTypes.number.isRequired,
    /** Shipping cost for the order */
    shippingCost: PropTypes.number.isRequired,
    /** Handling fee applied to the order */
    handlingFee: PropTypes.number.isRequired,
    /** Sales tax amount */
    salesTax: PropTypes.number.isRequired,
    /** Final total amount */
    total: PropTypes.number.isRequired,
};
export function CheckoutItems(props) {
    const { items, shippingTo, shippingWeight, subtotal_discount, subtotal, shippingCost, handlingFee, salesTax, total } = props;
    const cartItems = (_jsx("ul", { children: items.map((item) => {
            const itm = item;
            return _jsxs("li", { children: [itm.itemQuantity, " X - ", itm.itemTitle, " ( ", formatAsUSD(itm.itemCost), " )"] }, itm.itemID);
        }) }));
    const to = shippingTo;
    const addr = _jsxs(_Fragment, { children: [_jsx("div", { children: to.name }), _jsx("div", { children: to.street1 }), _jsxs("div", { children: [to.city, ", ", to.state, " ", to.zip] }), to.country ? _jsx("div", { children: to.country }) : null] });
    let checkoutTableData = [{
            "Name": "Shopping Cart Items : ",
            "Value": cartItems,
        }, {
            "Name": "Subtotal : ",
            "Value": formatAsUSD(subtotal),
        }, {
            "Name": "Shipping Address : ",
            "Value": addr,
        }, {
            "Name": "Email : ",
            "Value": to.email ? to.email : 'Not provided',
        }, {
            "Name": "Phone : ",
            "Value": to.phone ? to.phone : 'Not provided',
        }, {
            "Name": "Shipping Method : ",
            "Value": shippingTo?.shippingMethod ? `${shippingTo.shippingMethod}` : 'Not selected',
        }, {
            "Name": "Shipping Weight : ",
            "Value": shippingWeight ? `${formatAsHundredths(shippingWeight)} lb` : 'Not available',
        }, {
            "Name": "Discount : ",
            "Value": formatAsUSD(subtotal_discount),
        }, {
            "Name": "Shipping Cost : ",
            "Value": formatAsUSD(shippingCost),
        }, {
            "Name": "Handling Fee : ",
            "Value": formatAsUSD(handlingFee),
        }, /* {
            "Name": "Insurance Cost : ",
            "Value": formatAsUSD(checkoutData.insuranceCost ?? 0),
        }, */ /* {
            "Name": "Shipping Discount : ",
            "Value": formatAsUSD(checkoutData.shipping_discount ?? 0),
        }, */
        {
            "Name": "Sales Tax : ",
            "Value": formatAsUSD(salesTax),
        }, {
            "Name": "TOTAL : ",
            "Value": formatAsUSD(total),
        }];
    if (subtotal_discount == 0) {
        checkoutTableData = checkoutTableData.filter(obj => obj.Name !== "Subtotal Discount : ");
    }
    return (_jsx(Table, { id: "pixCheckout", data: checkoutTableData }));
}
/* ================================================ */
/* ======== SHOPPING CART BUTTON COMPONENT ======== */
/* ================================================ */
/**
 * CartButton — Render a cart button showing the current cart item count and navigates to the cart page when clicked.
 *
 * @param {string} [props.href] - Destination URL for the cart page.
 */
CartButton.propTypes = {
    /** Destination URL for viewing the shopping cart */
    href: PropTypes.string.isRequired,
};
export function CartButton(props) {
    const config = usePixelatedConfig();
    const [cartCount, setCartCount] = useState(0);
    useEffect(() => {
        // UPDATE CARTCOUNT STATES IF LOCALSTORAGE CHANGES
        function handleStorageChange() {
            setCartCount(getCartItemCount(getCart()));
        }
        window.addEventListener('storage', handleStorageChange);
        window.dispatchEvent(new Event('storage'));
        return () => {
            window.removeEventListener('storage', handleStorageChange);
        };
    }, []);
    useEffect(() => {
        if (cartCount && cartCount > 0) {
            MicroInteractions({ cartpulse: true });
        }
        else {
            MicroInteractions({ cartpulse: false });
        }
    }, [cartCount]);
    return (_jsx("div", { className: "pix-cart-button-wrapper", children: _jsxs("button", { className: "pix-cart-button", type: "button", id: "pix-cart-button", onClick: () => window.location.href = props.href, children: [_jsx(SmartImage, { src: "https://www.pixelated.tech/images/icons/cart-icon.png", title: "View Shopping Cart", alt: "View Shopping Cart", cloudinaryEnv: config?.integrations?.cloudinary?.product_env, cloudinaryDomain: config?.integrations?.cloudinary?.baseUrl, cloudinaryTransforms: config?.integrations?.cloudinary?.transforms }), _jsxs("span", { children: ["\u00A0", `(${cartCount})`] })] }) }));
}
/* ================================================ */
/* ====== VIEW ITEM DETAILS BUTTON COMPONENT ====== */
/* ================================================ */
/**
 * ViewItemDetails — Button to navigate to an item detail page for a given item ID.
 *
 * @param {string} [props.href] - Base URL for the item detail page.
 * @param {string} [props.itemID] - Item identifier appended to the URL when navigating.
 */
ViewItemDetails.propTypes = {
    /** Base URL for item details */
    href: PropTypes.string.isRequired,
    /** Item identifier to navigate to */
    itemID: PropTypes.string.isRequired,
};
export function ViewItemDetails(props) {
    const encodedItemID = encodeURIComponent(props.itemID);
    const isBaseStoreUrl = props.href === '/store' || props.href === '/store/';
    const targetUrl = isBaseStoreUrl ? `${props.href.replace(/\/+$/, '')}/${encodedItemID}` : props.href;
    return (_jsx("div", { children: _jsx(FormButton, { className: "pix-cart-button", type: "button", id: `btn-item-${props.itemID}`, text: "View Item Details", onClick: () => window.location.href = targetUrl }) }));
}
/* ================================================ */
/* ======== ADD TO CART BUTTON COMPONENT ========== */
/* ================================================ */
/**
 * AddToCartButton — Button that adds an item to the shopping cart and displays a confirmation modal.
 *
 * @param {function} [props.handler] - Handler function that performs the add-to-cart action (receives the item object).
 * @param {object} [props.item] - Shopping cart item object to add.
 * @param {string} [props.itemID] - Unique identifier for the item used for modal IDs and button IDs.
 */
AddToCartButton.propTypes = {
    /** Handler called to add the item to cart */
    handler: PropTypes.func.isRequired,
    /** Shopping cart item object */
    item: PropTypes.object.isRequired,
    /** Unique item identifier */
    itemID: PropTypes.string.isRequired,
};
export function AddToCartButton(props) {
    const [modalContent, setModalContent] = useState();
    useEffect(() => {
        const myContent = _jsxs("div", { className: "centered", children: [_jsx("br", {}), _jsx("br", {}), "Item ", props.itemID, " has been added to your cart.", _jsx("br", {}), _jsx("br", {}), GoToCartButton({ href: "/cart", itemID: props.itemID }), _jsx("br", {}), _jsx("br", {})] });
        setModalContent(myContent);
    }, [props.itemID]);
    function handleClick(e) {
        props.handler(props.item);
        handleModalOpen(e.nativeEvent, "-" + props.itemID);
    }
    return (_jsxs("div", { children: [_jsx(FormButton, { className: "pix-cart-button", type: "button", id: `btn-add-${props.itemID}`, text: "Add to Shopping Cart", onClick: (e) => handleClick(e) }), modalContent && _jsx(Modal, { modalContent: modalContent, modalID: "-" + props.itemID })] }));
}
/* ================================================ */
/* ======== GO TO  CART BUTTON COMPONENT ========== */
/* ================================================ */
/**
 * GoToCartButton — Button that navigates the user to the shopping cart page.
 *
 * @param {string} [props.href] - Destination URL for the shopping cart.
 * @param {string} [props.itemID] - ID used to compose button id attributes (not required for navigation).
 */
GoToCartButton.propTypes = {
    /** Cart page URL */
    href: PropTypes.string.isRequired,
    /** Item ID used for button id attributes */
    itemID: PropTypes.string.isRequired,
};
export function GoToCartButton(props) {
    return (_jsx("div", { children: _jsx(FormButton, { className: "pix-cart-button", type: "button", id: `btn-cart-${props.itemID}`, text: "Go to Shopping Cart", onClick: () => window.location.href = props.href }) }));
}
function formatReceiptAddress(shippingTo) {
    if (!shippingTo)
        return '';
    const parts = [
        shippingTo.street1,
        shippingTo.street2,
        shippingTo.city,
        shippingTo.state,
        shippingTo.zip,
        shippingTo.country,
    ].filter(Boolean);
    return parts.join(', ');
}
function parseMoney(value) {
    if (value === null || value === undefined || value === '')
        return undefined;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
}
function formatMoney(value) {
    const parsed = parseMoney(value);
    return parsed !== undefined ? formatAsUSD(parsed) : '';
}
export function buildReceiptData(orderData, config) {
    // Receipt rows are built from checkout data first; Square payload values are fallback-only.
    const payload = orderData?.data ? orderData.data : orderData;
    if (!payload)
        return null;
    const checkoutData = orderData?.checkoutData || payload?.checkoutData || payload?.purchase_units?.[0]?.shipping || {};
    const purchaseUnit = payload?.purchase_units?.[0] || {};
    const breakdown = purchaseUnit?.amount?.breakdown || {};
    const paymentCapture = payload.captureResponse?.payment || purchaseUnit?.payments?.captures?.[0] || payload;
    const orderId = paymentCapture?.id || payload?.id || payload?.orderID || payload?.order_id;
    const dateTime = paymentCapture?.created_at || paymentCapture?.create_time || paymentCapture?.updated_at || payload?.create_time || payload?.updated_at;
    const paymentStatus = paymentCapture?.status || payload?.status || 'Completed';
    const paymentMethod = payload?.sourceId ? 'Square' : 'PayPal';
    const currency = config?.integrations?.shoppingcart?.currency || paymentCapture?.amount_money?.currency || paymentCapture?.amount?.currency_code || purchaseUnit?.amount?.currency_code || 'USD';
    const amountValue = parseMoney(checkoutData?.total)
        ?? parseMoney(purchaseUnit?.amount?.value)
        ?? parseMoney(paymentCapture?.amount?.value)
        ?? (paymentCapture?.amount_money?.amount ? paymentCapture.amount_money.amount / 100 : undefined);
    const amount = amountValue !== undefined ? `${formatAsUSD(amountValue)} ${currency}` : '';
    const items = Array.isArray(checkoutData?.items)
        ? checkoutData.items.map((item) => ({
            itemID: item.itemID || item.name || item.id || '',
            itemTitle: item.itemTitle || item.description || item.name || '',
            itemQuantity: item.itemQuantity ?? item.quantity ?? 0,
            itemCost: parseMoney(item.itemCost ?? item.unit_amount?.value) ?? 0,
        }))
        : Array.isArray(purchaseUnit?.items)
            ? purchaseUnit.items.map((item) => ({
                itemID: item.name || item.id || '',
                itemTitle: item.description || item.name || '',
                itemQuantity: parseMoney(item.quantity) ?? 0,
                itemCost: parseMoney(item.unit_amount?.value) ?? 0,
            }))
            : [];
    const shippingTo = checkoutData?.shippingTo || purchaseUnit?.shipping?.address || {};
    const payerName = payload?.payer?.name || {};
    const fullName = checkoutData?.shippingTo?.name
        || payerName?.full_name
        || `${payerName?.given_name || ''} ${payerName?.surname || ''}`.trim();
    return {
        orderId,
        dateTime,
        paymentStatus,
        paymentMethod,
        orderSource: paymentMethod,
        amount,
        currency,
        fullName: fullName || 'Unknown',
        address: formatReceiptAddress(checkoutData?.shippingTo || purchaseUnit?.shipping?.address),
        phone: checkoutData?.shippingTo?.phone || payload?.payer?.phone?.phone_number?.national_number || '',
        email: checkoutData?.shippingTo?.email || payload?.payer?.email_address || payload?.buyer_email_address || '',
        shipping: formatMoney(checkoutData?.shippingCost ?? breakdown?.shipping?.value),
        handling: formatMoney(checkoutData?.handlingFee ?? breakdown?.handling?.value),
        tax: formatMoney(checkoutData?.salesTax ?? breakdown?.tax_total?.value),
        discount: formatMoney(checkoutData?.subtotal_discount ?? breakdown?.discount?.value),
        creditCardLast4: payload?.card?.details?.card?.last4 || paymentCapture?.card_details?.card?.last_4 || '',
        receiptUrl: paymentCapture?.receipt_url || '',
        items,
    };
}
export function renderReceiptTable(receipt) {
    const rows = [
        { Field: 'Order ID', Value: receipt.orderId || 'Unknown' },
        { Field: 'Date & Time', Value: receipt.dateTime || 'Unknown' },
        { Field: 'Full Name', Value: receipt.fullName || 'Unknown' },
        { Field: 'Address', Value: receipt.address || 'Unknown' },
        { Field: 'Email', Value: receipt.email || 'Unknown' },
        { Field: 'Phone', Value: receipt.phone || 'Unknown' },
        { Field: 'Items', Value: receipt.items && receipt.items.length > 0 ? (_jsx(Table, { id: "receipt-items-table", data: receipt.items.map((item) => ({
                    ID: item.itemID,
                    Title: item.itemTitle,
                    Quantity: item.itemQuantity,
                    'Price per item': formatAsUSD(item.itemCost),
                })) })) : 'None' },
    ];
    if (receipt.discount && receipt.discount !== '0.00' && receipt.discount !== '$0.00') {
        rows.push({ Field: 'Discounts', Value: `-${receipt.discount}` });
    }
    rows.push({ Field: 'Shipping', Value: receipt.shipping || '0.00' }, { Field: 'Handling', Value: receipt.handling || '0.00' }, { Field: 'Tax', Value: receipt.tax || '0.00' }, { Field: 'Total Amount', Value: receipt.amount || 'Unknown' }, { Field: 'Payment Method', Value: receipt.paymentMethod || 'Unknown' }, { Field: 'Credit Card (Last 4)', Value: receipt.creditCardLast4 || 'N/A' }, { Field: 'Payment Status', Value: receipt.paymentStatus || 'Unknown' });
    if (receipt.receiptUrl) {
        rows.push({
            Field: 'Receipt URL',
            Value: _jsx("a", { href: receipt.receiptUrl, target: "_blank", rel: "noopener noreferrer", children: "View receipt" }),
        });
    }
    return (_jsx("div", { style: { maxWidth: 768, margin: '0 auto' }, children: _jsx(Table, { id: "receipt-summary-table", data: rows }) }));
}
